import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { IAddress } from '@wix/cashier-core/dist/src/services/Order/types';
import {
  IAdditionalData,
  IRequestPaymentDetails,
  RedirectTarget,
} from '@wix/cashier-core/dist/src/services/Payment/types';
import { TokenizeResponse } from '@wix/payments-cc-form/dist/src/types/TokenizeResponse';
import { MFieldsNested } from '../types/MField';
import { deviceFingerprintGetter } from './deviceFingerprintGetter';
import { filterDefined } from '../../utils/filterDefined';

const getDeviceFingerprint = (): Promise<string> =>
  deviceFingerprintGetter.get().catch(() => undefined); // we can ignore missing fingerprint

const extractBillingAddressFromMFs = (
  mandatoryFields: MFieldsNested
): IAddress => {
  const { holderId, issuerBank, ...billingAddress } = mandatoryFields;

  return billingAddress;
};

export interface IBuildPaymentDetailsParams {
  paymentMethod: PaymentMethod;
  mFieldsNested?: MFieldsNested;
  locale: string;
  visitorId: string;
  saveCreditCard?: boolean;
  redirectTarget?: RedirectTarget;
  tokenizeResponse?: TokenizeResponse;
  installments?: number;
}

export const buildPaymentDetails = async (
  params: IBuildPaymentDetailsParams
): Promise<IRequestPaymentDetails> => {
  const {
    paymentMethod,
    mFieldsNested = {},
    installments,
    tokenizeResponse,
    redirectTarget,
    saveCreditCard,
  } = params;

  const paymentDetails: IRequestPaymentDetails = {
    paymentMethod,
    billingAddress: extractBillingAddressFromMFs(mFieldsNested),
  };

  if (redirectTarget) {
    paymentDetails.redirectTarget = redirectTarget;
  }

  const additionalData: IAdditionalData = {};

  if (paymentMethod === PaymentMethod.CreditCard) {
    paymentDetails.installments = installments;

    paymentDetails.card = filterDefined({
      numberToken: tokenizeResponse.numberToken,
      securityCodeToken: tokenizeResponse.securityCodeToken,
      holderName: tokenizeResponse.holderName,
      expiryMonth: tokenizeResponse.expiration?.month,
      expiryYear: tokenizeResponse.expiration?.year,
    });

    if (saveCreditCard) {
      paymentDetails.savePaymentMethod = true;
    }
  } else if (mFieldsNested.issuerBank) {
    additionalData['iDeal.issuerBank'] = mFieldsNested.issuerBank;
  }

  if (Object.keys(additionalData).length) {
    paymentDetails.additionalData = additionalData;
  }

  if (params.locale && params.visitorId) {
    paymentDetails.buyerInfo = {
      buyerId: params.visitorId,
      buyerLanguage: params.locale,
    };
  }

  const deviceFingerprint: string = await getDeviceFingerprint();

  if (deviceFingerprint) {
    paymentDetails.clientInfo = { deviceFingerprint };
  }

  return paymentDetails;
};
